import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import imageInputSvg from "@assets/images/image-input.svg";
import { ImageVersions } from "@domain/media/dtos";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { Primary } from "@ui/components/containers/primary";
import { DeleteIcon, EditIcon, ImageIcon } from "@ui/components/icons";
import { Row } from "@ui/components/layout";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { fadeIn } from "@ui/styles/keyframes";
import { gap } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { useEditorController } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";

type BannerEditorProps = React.HTMLAttributes<HTMLDivElement>;

export const BannerEditor: React.FC<BannerEditorProps> = ({ ...htmlProps }) => {
	const controller = useEditorController();
	const course = useObservable(controller.course);
	const [isHoverBanner, setIsHoverBanner] = useState(false);

	return (
		<Container {...htmlProps}>
			<Content>
				{course.banner ? (
					<StyledBackgroundImage image={course.banner} size={ImageVersions.BANNER}>
						<DeleteImageIcon onClick={() => controller.setBanner()}>
							<DeleteIcon />
						</DeleteImageIcon>
					</StyledBackgroundImage>
				) : (
					<FullSizeFileUpload
						onUpload={(media) => controller.setBanner(media)}
						onMouseEnter={() => setIsHoverBanner(true)}
						onMouseLeave={() => setIsHoverBanner(false)}
					>
						{(dragging: boolean, loading: boolean) => (
							<>
								<UploadContent dragging={dragging}>
									{loading ? <ClipLoader /> : <img src={imageInputSvg} />}
								</UploadContent>
								<QuickEdit isHover={isHoverBanner}>
									<EditIcon />
								</QuickEdit>
							</>
						)}
					</FullSizeFileUpload>
				)}
				<TitleWrapper>
					<b>{course.title || I18n.t("course.editor.title.missing")}</b>
				</TitleWrapper>
			</Content>
			<FileUpload onUpload={(media) => controller.setBanner(media)}>
				{(_, loading) => (
					<EditBanner>
						{loading ? (
							<ClipLoader size={18} />
						) : (
							<Row gap={6} align="center">
								<ImageIcon />
								<div>
									<b>{I18n.t("course.editor.banner.edit")}</b>
								</div>
							</Row>
						)}
					</EditBanner>
				)}
			</FileUpload>
		</Container>
	);
};

const Container = styled.div`
	${gap(20)};
`;

const Content = styled.div`
	position: relative;
	overflow: hidden;
	border-radius: 12px;
	height: 200px;
	width: 100%;
	background: ${({ theme }) => theme.colors.white};
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	width: 100%;
	height: 100%;
`;

const QuickEdit = styled(Primary)<{ isHover: boolean }>`
	position: absolute;
	right: 10px;
	top: 10px;
	cursor: pointer;
	border-radius: 13px;
	background-color: ${({ theme }) => theme.colors.secondary};
	display: flex;
	align-items: center;
	padding: 5px;
	transition: padding ease-out 0.2s;
	will-change: padding;

	${({ isHover }) =>
		isHover &&
		css`
			padding: 7px;
			border-radius: 15px;
		`}

	> svg {
		path {
			fill: ${({ theme }) => theme.colors.white};
		}
	}
`;

const EditBanner = styled(Primary)`
	color: ${({ theme }) => theme.colors.primary};
	max-width: 250px;

	svg {
		width: 13px;
		path {
			fill: ${({ theme }) => theme.colors.primary};
		}
	}
`;

const UploadContent = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "dragging",
})<{ dragging?: boolean }>`
	width: 100%;
	height: 100%;
	background-color: ${({ theme }) => theme.colors.primary};
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	overflow: hidden;

	transform: scale(${({ dragging }) => (dragging ? 0.85 : 1)});
	transition: transform 0.2s ease-out;

	img {
		position: absolute;
		z-index: ${Z.under};
		top: 50%;
		left: 50%;
		transition: transform 0.2s ease-in-out;
		transform-origin: 52% 46%;
		transform: translate(-50%, -50%) ${({ dragging }) => dragging && "scale(20)"};
	}
`;

export const FullSizeFileUpload = styled(FileUpload)`
	height: 100%;
`;

const TitleWrapper = styled(Primary)`
	pointer-events: none;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	background-image: linear-gradient(to top, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0));
	color: ${({ theme }) => theme.colors.white};
	padding: 26px 14px;
	display: flex;
	align-items: flex-end;
`;

const DeleteImageIcon = styled.div`
	position: absolute;
	right: 10px;
	top: 10px;
	cursor: pointer;

	@media (pointer: fine) {
		display: none;
	}

	${Container}:hover & {
		display: block;
	}

	animation: ${fadeIn} 0.2s linear;
	transition: transform 0.2s ease-in-out;
	&:hover {
		transform: rotate(90deg);
	}
`;
